// Track B (realtime-inbox migration) — pure, framework-free helpers for
// merging WS realtime events into the shapes InboxPage.jsx already renders.
// growchat-doc/realtime-inbox/spec-websocket-conversation-migration.md
//
// Kept dependency-free (no React/react-query imports) on purpose so these
// are trivially unit-testable without mounting anything.

// Confirmed by reading ai-receptionist-bot's app/db/repositories.py
// `_publish_message_ws_event()` call sites: the WS `conversation.message`
// event's `message` field is `message.model_dump(mode="json")` — the FULL
// MessageRecord (message_id, role, content, image_url, attachments,
// timestamp, metadata). The REST endpoint this replaces, GET
// /manager/{tenantId}/contacts/{contactId}/messages (contacts.py
// get_contact_messages()), returns a narrower per-message shape: only
// conversation_id/channel/role/content/image_url/timestamp — message_id,
// attachments, and metadata are NOT present in the REST response (confirmed
// by reading contacts.py's get_contact_messages(), which hand-builds each
// item with exactly those 6 keys). This is a spec-vs-code drift: the task
// spec's WS Message Contract implies the same shape as GET .../messages,
// but the two shapes differ.
//
// Normalizing to the REST shape here — while passing through message_id/
// attachments/metadata as bonus fields — keeps the merge target identical to
// what queryFn: getContactMessages() already produces, while letting
// InboxPage.jsx's existing getFileAttachments() (which already defensively
// checks Array.isArray(message.attachments)) opportunistically render
// WS-delivered file attachments it never had data for before over REST.
export function normalizeWsConversationMessage(event) {
  const message = event?.message || {}
  return {
    conversation_id: event?.conversation_id ?? null,
    channel: event?.channel ?? message.channel ?? null,
    role: message.role,
    content: message.content ?? '',
    image_url: message.image_url ?? null,
    timestamp: message.timestamp ?? null,
    ...(message.message_id ? { message_id: message.message_id } : {}),
    ...(Array.isArray(message.attachments) && message.attachments.length
      ? { attachments: message.attachments }
      : {}),
    ...(message.metadata && Object.keys(message.metadata).length ? { metadata: message.metadata } : {}),
  }
}

// A batch of images sent together all carry role='staff' and content='', so
// role+content+timestamp alone cannot tell them apart: `new Date().toISOString()`
// called in a loop usually returns the same millisecond, which collapsed two
// distinct images onto one identity and made the dedupe below drop one of them.
// The media URLs are what actually differ, so they join the identity. Every
// added term is `?? ''`-normalized, so a field that is absent on an optimistic
// append and explicitly null on the WS echo of the same message still produces
// the same identity and still dedupes.
function messageIdentity(message) {
  if (message?.message_id) return `id:${message.message_id}`
  const firstAttachmentUrl = message?.attachments?.[0]?.url ?? ''
  return `ts:${message?.role ?? ''}|${message?.content ?? ''}|${message?.timestamp ?? ''}|${message?.image_url ?? ''}|${firstAttachmentUrl}`
}

// Appends a normalized WS message onto an existing detailQuery cache entry
// (the object getContactMessages() resolves to:
// {contact_id, channel_filter, total_returned, messages}). Dedupes against
// whatever's already in `messages` (by message_id when present, else by
// role+content+timestamp) so a message that arrives via WS and then again
// via the 60s reconciliation poll — or an optimistic local append already
// sitting in the cache from the staff-send mutation — doesn't render twice.
export function mergeMessageIntoDetailCache(currentDetail, event) {
  const normalized = normalizeWsConversationMessage(event)
  const existingMessages = Array.isArray(currentDetail?.messages) ? currentDetail.messages : []
  const identity = messageIdentity(normalized)
  const alreadyPresent = existingMessages.some((m) => messageIdentity(m) === identity)
  if (alreadyPresent) return currentDetail ?? { messages: existingMessages }

  const messages = [...existingMessages, normalized]
  return currentDetail
    ? { ...currentDetail, messages, total_returned: messages.length }
    : { messages, total_returned: messages.length }
}
